import { z } from "zod";

/**
 * 配置中心的各组 schema。
 *
 * 🔴 **存进库之前一定要过这里。** 坏数据进了库就是整站行为异常，而且很难查。
 */

export const siteSettingsSchema = z.object({
  name: z.string().min(1).max(60),
  icp: z.string().max(60).default(""),
  contactPhone: z.string().max(20).default(""),
  /**
   * 对外网址，**分享出去的链接用它拼**（`https://mall.example.com`，结尾不要斜杠）。
   *
   * 🔴 **不填的话分享链接会用"当前浏览器地址栏里的域名"** ——
   *    商家在开发机 / 内网 IP / `127.0.0.1` 上打开后台，复制出来的专属链接就是
   *    `http://127.0.0.1:5183/i/XXXX`。发给客户的手机，那个地址指向的是
   *    **客户自己的设备**，必然打不开。而商家自己点是好的（他就在那台机器上），
   *    所以这种故障非常难被发现。
   *
   * ⚠️ 留空 = 退回当前域名（本机开发时最方便）。**上线必须填。**
   */
  baseUrl: z
    .string()
    .trim()
    .max(200)
    .refine((v) => v === "" || /^https?:\/\/[^\s/]+/.test(v), "要以 http:// 或 https:// 开头")
    .default(""),
});

export const bindingSettingsSchema = z.object({
  /** 🔴 默认 first_win（先绑定者优先），这是防抢客最主要的防线。 */
  conflictPolicy: z.enum(["first_win", "last_win"]).default("first_win"),
  /** null = 永久 */
  expiryDays: z.number().int().positive().nullable().default(null),
  breakPolicy: z.enum(["attach_parent", "detach", "manual"]).default("attach_parent"),
});

export const pricingSettingsSchema = z.object({
  defaultMarkupRate: z.number().min(0).max(10).default(0.15),
  belowCostPolicy: z.enum(["allow", "warn", "block"]).default("warn"),
  priceChangeBufferHours: z.number().int().min(0).max(720).default(24),
});

export const settlementSettingsSchema = z.object({
  /** 🔴 售后维权期。收货即结算的话，用户三天后退货钱已被提走，亏的是平台。 */
  afterSaleDays: z.number().int().min(0).max(90).default(15),
  minWithdrawal: z.number().int().nonnegative().default(10_000),
  withdrawalFeeRate: z.number().min(0).max(0.2).default(0),
});

export const commissionSettingsSchema = z.object({
  /** 参与分佣的层数。无限级链条，只有最近 N 级分钱。 */
  levels: z.number().int().min(1).max(10).default(3),
  /** 🔴 抽成叠加后被抽方至少留下的比例，行业通行是不低于 50%。 */
  minRetainRate: z.number().min(0.3).max(1).default(0.5),
  /** 🔴 只对直接下级计佣，不跨级层层抽 —— 与「以下线业绩计酬」划清界限。 */
  directOnly: z.boolean().default(true),
});

export const orgSettingsSchema = z.object({
  /** null = 关系链深度不限（行业做法是「无限级分销、三级分佣」）。 */
  maxDepth: z.number().int().min(1).max(20).nullable().default(null),
  distributorCanRecruit: z.boolean().default(true),
  auditDealer: z.boolean().default(true),
  auditDistributor: z.boolean().default(false),
});

export const fulfillmentSettingsSchema = z.object({
  shipBy: z.enum(["supplier_direct", "seller_stock"]).default("supplier_direct"),
  afterSaleBy: z.enum(["seller", "platform"]).default("seller"),
});

/**
 * 收款。
 *
 * 🔴 **这一组以前没有 schema，等于后台改不了** —— 而 `collectMode`
 *    是整个平台资金模型的总开关（钱经不经平台）。改不了就是没有。
 *
 * 🔴 **凭据不在这里。** appSecret 之类一律走环境变量 / Infisical：
 *    落进配置表意味着任何能读后台配置的人都能伪造「已付款」。
 *    这里只放能公开的策略。
 */
export const paymentSettingsSchema = z.object({
  /**
   * 🔴 offline_direct = 线上只建订单、收款走别的渠道，钱不经平台（本商城默认）。
   *    platform_gateway = 钱经平台收，再按价格链分账入各级余额。
   * ⚠️ 只影响新订单：在途的单按它下单时的模式走完，
   *    半路换模型会让一批订单既没分账也没人收款。
   */
  collectMode: z.enum(["offline_direct", "platform_gateway"]).default("offline_direct"),
  currentGateway: z.enum(["zhongzhuan", "qrcode"]).default("zhongzhuan"),
  /** 走线上通道时的订单有效期（分钟）。 */
  orderTimeoutMinutes: z.number().int().min(5).max(1440).default(15),
  /**
   * 线下收款的订单有效期（小时）。
   * ⚠️ 太短 → 买家钱都转出去了才发现单子过期、库存退了；
   *    太长 → 不打算付款的单把热销 SKU 的库存长期占死。
   */
  offlineTimeoutHours: z.number().int().min(1).max(720).default(72),
  splitEnabled: z.boolean().default(true),
  splitPerPieceMax: z.number().int().positive().default(19_990),
  splitMaxPieces: z.number().int().min(1).max(20).default(5),
  qrcodeUrl: z.string().max(500).nullable().default(null),
});

/**
 * 个税代扣。
 * 🔴 这组配置直接影响每个人到手多少钱，改之前必须让财务确认口径。
 */
export const taxSettingsSchema = z.object({
  method: z.enum(["none", "labor", "flat"]),
  /** 免扣线（分）。 */
  threshold: z.number().int().min(0),
  /** 核定比例，method=flat 时用。⚠️ 上限 30%：再高多半是填错了单位。 */
  flatRate: z.number().min(0).max(0.3),
});

/**
 * 外观 —— 配色 / 字体 / 货币。
 *
 * 🔴 **这三样都是"整个站点长什么样"，所以是平台后台的全站设置，
 *    不是每个用户自己挑。** 老板：「这个是平台管理后台来切换」。
 *
 * ⚠️ 三个都只影响**显示**，一分钱都不动：
 *    - `theme` 换的是 CSS 变量（`--brand-*`），不改任何布局
 *    - `script` 是简繁**字形**转换，不是翻译
 *    - `currency` 是**这个站点用哪种货币计价**，见下面的红字
 */
export const appearanceSettingsSchema = z.object({
  /** 配色。值要跟 `apps/web/src/styles.css` 里的 `[data-theme=...]` 对上。 */
  theme: z.enum(["terracotta", "ocean", "forest", "graphite", "plum"]).default("terracotta"),
  /** hans = 简体（默认）· hant = 繁体。 */
  script: z.enum(["hans", "hant"]).default("hans"),
  /**
   * 🔴 **这是"本站用哪种货币计价"，不是汇率换算。**
   *
   *    库里所有金额都存**分**，它们是哪种货币的分由这个开关声明。
   *    切换它**只换符号**（¥ / NT$），**绝不折算金额** ——
   *    折算意味着页面显示 NT$ 而实际按 CNY 扣钱，那是资金事故。
   *
   *    真要做多币种（同一批商品对不同地区显示不同价），
   *    那是另一件事：要汇率表、要下单时锁汇率、要结算按哪一种记账。
   */
  currency: z.enum(["CNY", "TWD"]).default("CNY"),
});

/**
 * 登录页那一屏（网页版左边那块品牌区）。
 *
 * 老板：「后台需要一个地方可以配置这 3 个东西，页面管理下的登录页管理」。
 * 指的就是登录页左边从上到下那三块：小标题 · 大标题 · 商品图。
 *
 * 🔴 **这几个字是写死在组件里的**，改一个字要发一次版 —— 而它是整个站
 *    第一眼看到的地方，运营改文案的频率远高于发版的频率。
 *
 * ⚠️ 出参会经 `/api/site-config` 给**未登录**的人（登录页本来就没登录），
 *    所以这一组里只放**对外可见的门面文案**，别往里塞任何经营参数。
 */
export const loginPageSettingsSchema = z.object({
  /** 大标题上面那行小字。 */
  eyebrow: z.string().max(40).default("装修建材 · 一站购齐"),
  /**
   * 大标题。
   * ⚠️ 用 `\n` 换行 —— 这一句是有意折行的（「装一套房要的建材 / 在这儿一次配齐」），
   *    让浏览器自己折的话，屏宽一变断句就散了。
   */
  headline: z.string().max(120).default("装一套房要的建材\n在这儿一次配齐"),
  /**
   * 商品图拼贴怎么来。
   * - `sales` 按销量取前几名（默认，会随销量变）
   * - `manual` 只用下面指定的那几件（老板挑定，不会自己变）
   * - `off` 不显示这一块
   *
   * 🔴 `off` 和"取不到图"是两回事：取不到图时组件本来就整块不出现
   *    （登录页出破图比没有图更糟），这个开关是**明确不要**。
   */
  showcase: z.enum(["sales", "manual", "off"]).default("sales"),
  /** `showcase=manual` 时用这几件商品的主图。⚠️ 顺序就是显示顺序。 */
  showcaseProductIds: z.array(z.number().int().positive()).max(6).default([]),
});

/**
 * 首页「按你家的空间挑」那一排。
 *
 * 🔴 **`key` 是全站共用的词表，不是显示用的标签。**
 *    商品上打的 `products.spaces`、案例上的 `space`、商品列表的筛选，
 *    比的都是这个 key。改了它，已经打过标签的商品就从那一格里消失了 ——
 *    所以后台**只让改名字、副标题、图标和顺序**，key 建好就不给改。
 *    （网页版商品列表原先自己写了一份中文 key（`space: "客厅"`），
 *    而商品上存的是 `living` —— 点了永远筛不出东西。收拢成一份就是为了这个。）
 *
 * 🔴 **图标要能在后台换**，所以存的是一个字符串而不是写死的组件。两种画法：
 *    - `sofa` —— 站内自带的矢量图（36 个，装修建材主题，
 *      在 `apps/web/src/components/space-icons.data.ts`）
 *    - `emoji:🛋️` —— emoji 画法，最早那一版的样子
 *
 *    **两者的 SVG / 字符都在仓库里，不引任何外部图标脚本** —— 引外链等于把
 *    首页的完整性挂在别人的服务器上，对方一挂或者改了编号，首页就缺一块。
 *
 * ⚠️ 兼容老值 `builtin:sofa`：早先图标有两个来源，值上带前缀。前台会剥掉它。
 */
export const spaceItemSchema = z.object({
  /** 只允许小写字母 / 数字 / 下划线 —— 它会出现在地址栏上（`?space=living`）。 */
  key: z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,23}$/, "只能用小写字母开头，后面接小写字母、数字或下划线"),
  label: z.string().min(1).max(10),
  /** 网页版那一格底下的一行小字（手机版放不下，不显示）。 */
  hint: z.string().max(24).default(""),
  /**
   * ⚠️ `emoji:` 后面必须真有个字符 —— 只有前缀的话前台画出来是个空格，
   *    首页那一格看着就像图标丢了。
   */
  icon: z
    .string()
    .min(1)
    .max(60)
    .refine((v) => !v.startsWith("emoji:") || v.length > "emoji:".length, "还没填 emoji"),
  /** 关掉 = 首页不出这一格。⚠️ 不是删 —— 删了商品上的标签就成了孤儿。 */
  enabled: z.boolean().default(true),
});

export const spacesSettingsSchema = z.object({
  items: z.array(spaceItemSchema).min(1).max(12),
});

export const SETTING_SCHEMAS = {
  site: siteSettingsSchema,
  appearance: appearanceSettingsSchema,
  loginPage: loginPageSettingsSchema,
  spaces: spacesSettingsSchema,
  binding: bindingSettingsSchema,
  pricing: pricingSettingsSchema,
  settlement: settlementSettingsSchema,
  commission: commissionSettingsSchema,
  org: orgSettingsSchema,
  fulfillment: fulfillmentSettingsSchema,
  payment: paymentSettingsSchema,
  tax: taxSettingsSchema,
} as const;

export type SettingGroupKey = keyof typeof SETTING_SCHEMAS;

export const saveSettingsSchema = z.object({
  value: z.record(z.string(), z.unknown()),
  /** 🔴 乐观锁：打开表单时的 updatedAt，与库里不一致就拒绝保存。 */
  expectedUpdatedAt: z.string().nullable().optional(),
});
